import { isAfter } from 'date-fns';
import {
  ICustomer,
  IUser,
  MAKE,
  OrderCreationType,
  OrderStatus,
  VehicleReserveStatus,
  getMakeCodeByName,
} from 'next-common';
import {
  getCustomerName,
  hasReservationBlockingCancellation,
  hasReservationWaitingState,
  isOffer,
  offerStati,
  reservationStatus,
} from '../../../../utils';
import { Icon, IconType, Menu } from '@repo/ui/components';
import { TMenuItem, Alignment, cn, formatDateInput } from 'types';

export const CANCEL_RESERVATION_TEXT = 'Slett reservasjon';
export const DELETE_OFFER_TEXT = 'Slett tilbud';
export const RESUME_ORDER_TEXT = 'Gjennoppta';
export const SHOW_OFFER_TEXT = 'Vis kundekort';

export const customerFormatter = (customer: Partial<ICustomer>) => {
  const bosCustomer = customer?.bosCustomer;
  return getCustomerName(bosCustomer) ?? '-';
};

export const salesPersonFormatter = (salesPerson: Partial<IUser>) => {
  const fullName =
    salesPerson?.firstName && salesPerson?.lastName && `${salesPerson.firstName} ${salesPerson.lastName}`;
  const userIdent = salesPerson?.ident;
  const userEmail = salesPerson?.email && salesPerson?.email !== 'null' ? salesPerson.email : null;

  return (fullName || userIdent || userEmail) ?? '-';
};

export const dateFormatter = (timestamp?: string) => {
  if (!timestamp) return '-';
  return formatDateInput(timestamp).formatted;
};

const statusFormatter = (label: string, color: string, borderColor?: string, key?: string) => {
  const renderStatusFormat = () => {
    switch (key) {
      case 'SIGNED':
      case 'RESERVED':
        return (
          <div className="bg-white">
            <Icon icon={IconType.NewNextCheckFilled} fill={'var(--color-komorebi)'} width={24} height={24} />
          </div>
        );
      case 'RESERVATION_CANCELED':
        return (
          <div className="bg-white">
            <Icon icon={IconType.NewNextCancelSolid} fill={'var(--color-poppy)'} width={24} height={24} />
          </div>
        );
      default:
        return (
          <div
            className={'text-sm font-semibold leading-[170%] rounded-full flex items-center px-3'}
            style={{ backgroundColor: label ? color : '', border: label ? `1px solid ${borderColor}` : '' }}
          >
            {label ?? '-'}
          </div>
        );
    }
  };
  return renderStatusFormat();
};

export const offerStatusFormatter = (code: string) => {
  const status = offerStati.find((s) => s.key === code) || {
    label: code,
    color: offerStati[0].color,
    borderColor: offerStati[0].borderColor,
  };
  return statusFormatter(status.label, status.color, status?.borderColor);
};

export const menuButtonsFormatter = (menuButtons: TMenuItem[], actionCallback = null, value: any, rowData: any) => {
  menuButtons = menuButtons
    .filter(({ text }) => {
      const orderStatus: OrderStatus = OrderStatus[rowData.status as string];
      const reserveStatus: VehicleReserveStatus =
        VehicleReserveStatus[rowData?.reserveDetails?.vehicleReserveStatus as string];

      switch (text) {
        case CANCEL_RESERVATION_TEXT:
          // Remove `remove reservation` button if no vehicle is reserved
          return reserveStatus === VehicleReserveStatus.RESERVED;
        case DELETE_OFFER_TEXT:
          // Remove `delete offer` button if not offer or has reservation
          return (
            isOffer(orderStatus) &&
            !hasReservationBlockingCancellation(rowData?.reserveDetails) &&
            !rowData?.isEditOrder
          );
        case RESUME_ORDER_TEXT:
          // Remove `continue offer` button if not offer or has reservation
          return isOffer(orderStatus) && !hasReservationWaitingState(rowData?.reserveDetails);
        default:
          return true;
      }
    })
    .map((item) => ({ ...item, context: rowData }));

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Menu
        callback={actionCallback}
        items={menuButtons}
        align={Alignment.RIGHT}
        icon={IconType.NewNextChevronRight}
        triggerIconSize={{
          width: 24,
          height: 24,
        }}
        itemIconSize={{ width: 24, height: 24 }}
        className="z-20"
        headerClassName="px-5 min-w-[11.875rem] text-base py-3 font-bold"
        itemClassName="px-5 min-w-[11.875rem] text-base gap-3 py-3 font-bold"
      />
    </button>
  );
};

export const expiryDateFormatter = (momentDate?: Date) => {
  const isValidDate = momentDate instanceof Date && !isNaN(momentDate.getTime());
  const hasExpired = isValidDate ? isAfter(new Date(), momentDate) : false;
  return (
    <div
      className={cn({
        'text-alertRed': hasExpired,
      })}
    >
      {isValidDate ? formatDateInput(momentDate.toISOString()).formatted : ''}
    </div>
  );
};

export const makeIcon = (make: MAKE) => {
  const iconProps = { width: 25, height: 25 };
  switch (getMakeCodeByName(make)) {
    case MAKE.Mercedes:
      return <Icon icon={IconType.MercedesBenz} {...iconProps} />;
    case MAKE.Kia:
      return <Icon icon={IconType.Kia} {...iconProps} />;
    case MAKE.Peugeot:
      return <Icon icon={IconType.Peugeot} {...iconProps} />;
    case MAKE.Citroen:
      return <Icon icon={IconType.Citroen} {...iconProps} />;
    case MAKE.Opel:
      return <Icon icon={IconType.Opel} {...iconProps} />;
    case MAKE.Smart:
      return <Icon icon={IconType.Smart} {...iconProps} />;
    default:
      return make?.substring(0, 3) ?? null;
  }
};

export const reservationStatusFormatter = (code: string) => {
  const status = reservationStatus.find((s) => s.key === code) || {
    label: code,
    color: offerStati[0].color,
    borderColor: offerStati[0].borderColor,
    key: code,
  };
  return statusFormatter(status.label, status.color, status.borderColor, status.key);
};

export const offertypeFormatter = (type: string) => {
  switch (type) {
    case OrderCreationType.CUSTOMER:
      return 'Nybil';
    case OrderCreationType.STOCK:
      return 'Lagerbil';
    case OrderCreationType.DEMO:
      return 'Demobil';
    case OrderCreationType.USED:
      return 'Bruktbil';
    default:
      return 'Nybil';
  }
};

export const dealerFormatter = (dealerId: string, row?: any) => {
  return (
    <div
      key={row}
      className="text-sm bg-neutralGrey rounded-full px-3 py-[2px] border border-neutralPlus flex items-center"
    >
      {dealerId ?? '-'}
    </div>
  );
};
